/* eslint complexity: ["error", { max: 16 }] -- Due to flag-off legacy path, flag-on intake lock, flag-on fresh-start path, and provider exemption */
import { TaskStatusLabel, TaskSubcategory } from "@/constants/Tasks";

const getLegacyLockResult = ({
  currentUserId,
  isSupport,
  lastUpdatedByUserId,
  lastUpdatedByUserType,
}: Pick<
  IsTaskEditableOptions,
  | "currentUserId"
  | "isSupport"
  | "lastUpdatedByUserId"
  | "lastUpdatedByUserType"
>): IsTaskEditableResult => {
  const isSupportPatientAlreadyEdited =
    isSupport &&
    lastUpdatedByUserId !== currentUserId &&
    lastUpdatedByUserType !== "provider";

  return { isEditable: !isSupportPatientAlreadyEdited, isFreshStart: false };
};

const isTaskEditable = ({
  currentUserId,
  isAssignAssessmentsToProxySupportsEnabled,
  isCore,
  isSupport,
  lastUpdatedByUserId,
  lastUpdatedByUserType,
  taskStatus,
  taskSubCategoryName,
}: IsTaskEditableOptions): IsTaskEditableResult => {
  const isAssessment =
    taskSubCategoryName === TaskSubcategory.SubCategoryTypeAssessment ||
    taskSubCategoryName === TaskSubcategory.IntakeSurvey;

  // When flag is off, preserve original edit-lock behavior
  if (!isAssignAssessmentsToProxySupportsEnabled) {
    if (
      !lastUpdatedByUserId ||
      !isAssessment ||
      taskStatus === TaskStatusLabel.ToDo
    )
      return { isEditable: true, isFreshStart: false };

    return getLegacyLockResult({
      currentUserId,
      isSupport,
      lastUpdatedByUserId,
      lastUpdatedByUserType,
    });
  }

  // Flag is on: non-core assessments and intake surveys keep the legacy lock (no fresh-start)
  if (!isCore) {
    if (
      !lastUpdatedByUserId ||
      !isAssessment ||
      taskStatus === TaskStatusLabel.ToDo ||
      taskStatus === TaskStatusLabel.Done
    )
      return { isEditable: true, isFreshStart: false };

    return getLegacyLockResult({
      currentUserId,
      isSupport,
      lastUpdatedByUserId,
      lastUpdatedByUserType,
    });
  }

  // Flag is on: fresh-start behavior for core assessments
  if (
    taskStatus === TaskStatusLabel.ToDo ||
    taskStatus === TaskStatusLabel.Done
  )
    return { isEditable: true, isFreshStart: false };

  if (lastUpdatedByUserId === currentUserId) {
    return { isEditable: true, isFreshStart: false };
  }

  // Provider drafts are exempt from fresh-start — let the user resume rather than wipe provider work
  if (lastUpdatedByUserType === "provider") {
    return { isEditable: true, isFreshStart: false };
  }

  // Another respondent started this assessment — or we don't know who did
  // (lastUpdatedByUserId may be undefined if the query hasn't resolved yet).
  // Either way, start fresh so the current user never sees someone else's answers.
  return { isEditable: true, isFreshStart: true };
};

export { isTaskEditable };
